import { updateSideItemHeight } from '../SideItemOption.utils';

import { BACKSPLASH_DEFAULT_VALUE, CORNER_RADIUS_SIGN } from '~/core/constants';
import { EditableCornerLabel } from '~/core/engine/EditableLabel';
import { ShapeType, SideItemOption, SideItemType } from '~/core/enums';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { useSideItemValidation } from '~/core/hooks/useSideItemValidation';
import {
  ArrowInfo,
  CornerInfo,
  DrawingEdge,
  DrawingShape,
  RemovedEdgeAndCorners,
  SideItemInfoModel,
} from '~/core/models';
import { DEFAULT_SIDE_ITEM } from '~/core/utils';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { selectSelectedColor } from '~/redux/slices/color';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import { useDrawingShapeContext, usePricedProductsContext } from '~contexts';

const useSideItems = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);
  const { validateAndNotifySideItemHeightError } = useSideItemValidation();
  const { registerSideItemAction } = useRegisterUserAction();
  const {
    getters: { drawingShapes },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  usePricedProductsContext();

  const selectedColor = useAppSelector(selectSelectedColor);

  const cleanEdgeAndCornersHistory = (
    edgeId: DrawingEdge['id'],
    shape: DrawingShape
  ): void => {
    shape.removedElementsHistory = {
      edgesAndCorners: (
        shape.removedElementsHistory?.edgesAndCorners ?? []
      ).filter(({ drawingEdge }) => drawingEdge.id !== edgeId),
    };
  };

  /**
   * adds a copy of the drawingEdge to the removedElementsHistory list
   */
  const addSideItemToHistory = (
    shape: DrawingShape,
    edge: DrawingEdge,
    sideItemType: SideItemType
  ) => {
    if (!shape.removedElementsHistory) {
      return;
    }

    const arrowInfo = shape.arrowsInfo?.find(
      (info) => info.drawingEdgeId === edge.id
    );
    // EE-968 ensure corners are grouped by start:first to end:last
    // helps with backsplash arrow display
    const appliedCorners = [edge.startCornerId, edge.endCornerId].map(
      (id) => shape.cornerMap[id]
    ) as [CornerInfo, CornerInfo];
    const newEdgeAndCorners: RemovedEdgeAndCorners = {
      sideItemType,
      drawingEdge: { ...edge },
      arrowInfo,
      appliedCorners,
    };

    // remove existing edge before adding
    cleanEdgeAndCornersHistory(edge.id, shape);
    shape.removedElementsHistory.edgesAndCorners.push(newEdgeAndCorners);
  };

  const prepareEdgeForUpdate = (shape: DrawingShape, edge: DrawingEdge) => {
    const arrow = shape.arrowsInfo?.find(
      (arrowInfo) => arrowInfo?.drawingEdgeId === edge.id
    );

    // reduce the history of removed edgesAndCorners
    // by having one side per edge
    cleanEdgeAndCornersHistory(edge.id, shape);

    // update cornerRadii list with selectedEdge info
    [edge.startCornerId, edge.endCornerId]
      .filter((id) => id && id in shape.cornerMap)
      .forEach((id) => (shape.cornerMap[id].isVisibleInCanvas = false));

    // remove drawingEdgeId from arrowInfo
    // for Area calculation and marked drawingEdge as not visible in canvas
    if (arrow) {
      arrow.isActiveForEdgeCalc = false;
      arrow.isVisibleInCanvas = true;
      edge.sideItem = { ...DEFAULT_SIDE_ITEM };
      edge.isVisibleInCanvas = false;
    }
  };

  const replaceWithEditedCorner = (
    editedCorner: CornerInfo,
    shape: DrawingShape
  ) => {
    if (editedCorner.id in shape.cornerMap) {
      shape.cornerMap[editedCorner.id] = editedCorner;
    }
    updateDrawingShapes([...drawingShapes]);
    dispatch(updateNeedRedrawCanvas(true));
    canvas.renderAll();
  };

  const resetDrawingEdge = (edgeId: DrawingEdge['id']): void => {
    const updatedShapes = drawingShapes.map((shape) => {
      const removedEdgeAndCornerInfo =
        shape.removedElementsHistory?.edgesAndCorners.find(
          ({ drawingEdge }) => drawingEdge.id === edgeId
        ) as RemovedEdgeAndCorners;

      if (shape.type === ShapeType.FAUX_SHAPE || !removedEdgeAndCornerInfo) {
        return shape;
      }

      const cornerIds = Object.keys(shape.cornerMap);
      const drawingEdgeId = removedEdgeAndCornerInfo.drawingEdge.id;

      // reset the `isVisible` flag from arrowInfo
      shape.arrowsInfo
        ?.filter((arrowInfo) => arrowInfo.drawingEdgeId === drawingEdgeId)
        .forEach((arrowInfo) => {
          arrowInfo.isActiveForEdgeCalc = true;
          arrowInfo.isVisibleInCanvas = true;
        });

      // reset the `isVisible` flag from drawingEdge
      shape.drawingEdges
        .filter(({ id }) => id === drawingEdgeId)
        .forEach((drawingEdge) => {
          drawingEdge.sideItem = { ...DEFAULT_SIDE_ITEM };
          drawingEdge.isVisibleInCanvas = true;
        });

      // reset cornerRadii by setting isVisibleInCanvas to true
      removedEdgeAndCornerInfo.appliedCorners
        .filter((appliedCorner) => appliedCorner.id in shape.cornerMap)
        .map((appliedCorner) => shape.cornerMap[appliedCorner.id])
        .forEach((cornerInfo) => {
          // corner.isVisibleInCanvas = true
          // if two edges share the same corner (startCorner and endCorner)
          // and both are visible in canvas
          cornerInfo.isVisibleInCanvas = shape.drawingEdges
            .reduce(
              ([l, r], { isVisibleInCanvas: isVisible, ...edge }) => [
                l || (isVisible && cornerInfo.id === edge.startCornerId),
                r || (isVisible && cornerInfo.id === edge.endCornerId),
              ],
              [false, false]
            )
            .every(Boolean);

          if (cornerInfo.isVisibleInCanvas) {
            shape.editableLabels.push(
              new EditableCornerLabel(
                shape,
                cornerInfo,
                CORNER_RADIUS_SIGN,
                replaceWithEditedCorner,
                canvas,
                selectedColor?.overlayColor,
                cornerIds.findIndex((id) => id === cornerInfo.id)
              )
            );
          }
        });

      cleanEdgeAndCornersHistory(edgeId, shape);

      return shape;
    });

    updateDrawingShapes([...updatedShapes]);
    dispatch(updateNeedRedrawCanvas(true));
  };

  enum ShapeSideType {
    rectangle = 'rectangle',
    none = 'none',
  }

  const updateDrawingEdgeWithSideItem = (
    shapeSide: ShapeSideType,
    sideItem: SideItemInfoModel
  ) => {
    const updateShapeEdgesWith: Record<
      ShapeSideType,
      (drawingEdge: DrawingEdge, arrowInfo: ArrowInfo) => void
    > = {
      [ShapeSideType.rectangle]: (drawingEdge, arrowInfo) => {
        if (sideItem.backsplash?.height) {
          drawingEdge.sideItem.depth = sideItem.backsplash.height;
          drawingEdge.sideItem.type = SideItemOption.BACKSPLASH;
        }
        if (sideItem.waterfallHeight) {
          drawingEdge.sideItem.depth = sideItem.waterfallHeight;
          drawingEdge.sideItem.type = SideItemOption.WATERFALL;
        }
        arrowInfo.isVisibleInCanvas = false;
      },
      [ShapeSideType.none]: (drawingEdge) => {
        drawingEdge.sideItem = { ...DEFAULT_SIDE_ITEM };
        drawingEdge.isVisibleInCanvas = false;
      },
    };
    const dShapes = drawingShapes.map((shape) => {
      const drawingEdge = shape.drawingEdges.find(
        (edge) => edge.id === sideItem.drawingEdgeId
      );
      const arrowInfo = shape.arrowsInfo?.find(
        (arrow) => arrow.drawingEdgeId === sideItem.drawingEdgeId
      );

      if (shape.type !== ShapeType.FAUX_SHAPE && drawingEdge && arrowInfo) {
        prepareEdgeForUpdate(shape, drawingEdge);
        updateShapeEdgesWith[shapeSide](drawingEdge, arrowInfo);
        addSideItemToHistory(shape, drawingEdge, sideItem.sideType);
      }

      return shape;
    });

    updateDrawingShapes([...dShapes]);
    dispatch(updateNeedRedrawCanvas(true));
  };

  const toggleSideOption = (sideItem: SideItemInfoModel): void => {
    const shapeSide: Record<SideItemType, ShapeSideType> = {
      [SideItemType.WALL_SIDE]: ShapeSideType.none,
      [SideItemType.APPLIANCE_SIDE]: ShapeSideType.none,
      [SideItemType.WATERFALL]: ShapeSideType.rectangle,
      [SideItemType.END_CAP]: ShapeSideType.none,
      [SideItemType.END_SPLASH]: ShapeSideType.none,
    };

    if (sideItem.checked) {
      updateDrawingEdgeWithSideItem(shapeSide[sideItem.sideType], sideItem);
    } else {
      resetDrawingEdge(sideItem.drawingEdgeId);
    }
    registerSideItemAction(sideItem.sideType);
  };

  const toggleBacksplash = (
    sideItem: SideItemInfoModel,
    isChecked: boolean
  ) => {
    const updatedSideItem = {
      ...sideItem,
      backsplash: {
        checked: isChecked,
        height: BACKSPLASH_DEFAULT_VALUE,
      },
    };

    updateDrawingEdgeWithSideItem(
      isChecked ? ShapeSideType.rectangle : ShapeSideType.none,
      updatedSideItem
    );
    registerSideItemAction('backsplashWallSide');
  };

  const updateHeight = (sideItem: SideItemInfoModel) => (value: number) => {
    const updatedSideItem = updateSideItemHeight(sideItem, value);

    if (
      sideItem.sideType === SideItemType.WATERFALL &&
      sideItem.waterfallHeight
    ) {
      validateAndNotifySideItemHeightError(SideItemOption.WATERFALL, value);
    }

    if (
      sideItem.sideType === SideItemType.WALL_SIDE &&
      sideItem.backsplash?.checked &&
      sideItem.backsplash?.height
    ) {
      validateAndNotifySideItemHeightError(SideItemOption.BACKSPLASH, value);
    }

    updateDrawingEdgeWithSideItem(ShapeSideType.rectangle, updatedSideItem);
    registerSideItemAction(
      sideItem.sideType === SideItemType.WALL_SIDE
        ? 'backsplashHeight'
        : 'waterFallPanelHeight'
    );
  };

  return {
    toggleBacksplash,
    toggleSideOption,
    updateHeight,
  };
};

export default useSideItems;
